import {
  batchDeleteDevices,
  batchLifecycleEvents,
  batchPatchDevices,
  createDevice,
  deleteDevice,
  fetchDevice,
  fetchDeviceFieldOptions,
  fetchDeviceFields,
  fetchDeviceHistory,
  fetchDevices,
  fetchOrganizationOptions,
  fetchShipments,
  patchDevice,
  patchShipmentDates,
  postLifecycleEvent,
  restoreDevice,
} from '@/features/device-management/api/deviceManagement';
import { DEVICE_MANAGEMENT_QUERY_KEY } from '@/features/device-management/config/deviceManagement';
import { deviceLifecycleErrorCode } from '@/features/device-management/lib/deviceLifecycle';
import type {
  BatchDeleteInput,
  BatchLifecycleInput,
  BatchPatchInput,
  Device,
  DeviceHistoryParams,
  DeviceListParams,
  DevicePatchInput,
  LifecycleEventInput,
  ShipmentDatesInput,
  ShipmentListParams,
} from '@/features/device-management/types/deviceManagement';
import { SUPERVISOR_KEY } from '@/features/supervisor/config/constants';
import type { SupportedLocale } from '@/shared/types/i18n';
import {
  type QueryClient,
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

export const deviceManagementKeys = {
  all: DEVICE_MANAGEMENT_QUERY_KEY,
  list: (params: DeviceListParams) => [...DEVICE_MANAGEMENT_QUERY_KEY, 'list', params] as const,
  detail: (deviceId: string, timeZone: string) =>
    [...DEVICE_MANAGEMENT_QUERY_KEY, 'detail', deviceId, timeZone] as const,
  shipments: (params: ShipmentListParams) =>
    [...DEVICE_MANAGEMENT_QUERY_KEY, 'shipments', params] as const,
  history: (deviceId: string, params: DeviceHistoryParams) =>
    [...DEVICE_MANAGEMENT_QUERY_KEY, 'history', deviceId, params] as const,
  organizations: (operation: 'read' | 'manage', q: string) =>
    [...DEVICE_MANAGEMENT_QUERY_KEY, 'organizations', operation, q] as const,
  fields: (locale: SupportedLocale) => [...DEVICE_MANAGEMENT_QUERY_KEY, 'fields', locale] as const,
  fieldOptions: (field: string, locale: SupportedLocale, q: string) =>
    [...DEVICE_MANAGEMENT_QUERY_KEY, 'field-options', field, locale, q] as const,
};

function deviceQueryOptions(params: DeviceListParams) {
  return {
    queryKey: deviceManagementKeys.list(params),
    queryFn: ({ pageParam, signal }: { pageParam: string | undefined; signal: AbortSignal }) =>
      fetchDevices({ ...params, cursor: pageParam }, { signal }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page: { nextCursor: string | null }) => page.nextCursor ?? undefined,
    throwOnError: false,
  };
}

export function deviceListQuery(params: DeviceListParams) {
  return { ...deviceQueryOptions(params), placeholderData: keepPreviousData };
}

export function devicePickerQuery(params: DeviceListParams) {
  return deviceQueryOptions(params);
}

export function useDevices(params: DeviceListParams, enabled = true) {
  return useInfiniteQuery({ ...deviceListQuery(params), enabled });
}

/** A remote selection source intentionally clears results when its query changes. */
export function useDevicePicker(params: DeviceListParams, enabled = true) {
  return useInfiniteQuery({ ...devicePickerQuery(params), enabled });
}

export function deviceQuery(deviceId: string, timeZone: string) {
  return {
    queryKey: deviceManagementKeys.detail(deviceId, timeZone),
    queryFn: ({ signal }: { signal: AbortSignal }) => fetchDevice(deviceId, timeZone, { signal }),
  };
}

export function useDevice(deviceId: string, timeZone: string, enabled = true) {
  return useQuery({ ...deviceQuery(deviceId, timeZone), enabled });
}

export function useDeviceShipments(params: ShipmentListParams, enabled = true) {
  return useInfiniteQuery({ ...deviceShipmentsQuery(params), enabled });
}

export function deviceShipmentsQuery(params: ShipmentListParams) {
  return {
    queryKey: deviceManagementKeys.shipments(params),
    queryFn: ({ pageParam, signal }: { pageParam: string | undefined; signal: AbortSignal }) =>
      fetchShipments({ ...params, cursor: pageParam }, { signal }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page: { nextCursor: string | null }) => page.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
    throwOnError: false,
  };
}

export function useDeviceHistory(deviceId: string, params: DeviceHistoryParams, enabled = true) {
  return useInfiniteQuery({
    queryKey: deviceManagementKeys.history(deviceId, params),
    queryFn: ({ pageParam, signal }) =>
      fetchDeviceHistory(deviceId, { ...params, cursor: pageParam }, { signal }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    enabled,
    placeholderData: keepPreviousData,
  });
}

export function useDeviceOrganizations(operation: 'read' | 'manage', q = '', enabled = true) {
  return useInfiniteQuery({
    queryKey: deviceManagementKeys.organizations(operation, q),
    queryFn: ({ pageParam, signal }) =>
      fetchOrganizationOptions({ operation, q: q || undefined, cursor: pageParam }, { signal }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    enabled,
  });
}

export function useDeviceFields(locale: SupportedLocale) {
  return useQuery({
    queryKey: deviceManagementKeys.fields(locale),
    queryFn: ({ signal }) => fetchDeviceFields(locale, { signal }),
  });
}

export function useDeviceFieldOptions(
  field: string,
  locale: SupportedLocale,
  q = '',
  enabled = true,
) {
  return useInfiniteQuery({
    queryKey: deviceManagementKeys.fieldOptions(field, locale, q),
    queryFn: ({ pageParam, signal }) =>
      fetchDeviceFieldOptions(field, { locale, q: q || undefined, cursor: pageParam }, { signal }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    enabled,
  });
}

function invalidateDeviceData(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: deviceManagementKeys.all });
  void queryClient.invalidateQueries({ queryKey: [SUPERVISOR_KEY] });
}

function cacheDevice(queryClient: QueryClient, device: Device, timeZone: string) {
  queryClient.setQueryData(deviceManagementKeys.detail(device.deviceId, timeZone), device);
}

export function useCreateDevice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createDevice,
    onSuccess: (device, variables) => {
      cacheDevice(queryClient, device, variables.timeZone);
      invalidateDeviceData(queryClient);
    },
  });
}

export function usePatchDevice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ deviceId, body }: { deviceId: string; body: DevicePatchInput }) =>
      patchDevice(deviceId, body),
    onSuccess: (device, variables) => {
      cacheDevice(queryClient, device, variables.body.timeZone);
      invalidateDeviceData(queryClient);
    },
  });
}

export function useDeleteDevice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteDevice,
    onSuccess: () => {
      invalidateDeviceData(queryClient);
    },
  });
}

export function useRestoreDevice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ deviceId, timeZone }: { deviceId: string; timeZone: string }) =>
      restoreDevice(deviceId, timeZone),
    onSuccess: (device, variables) => {
      cacheDevice(queryClient, device, variables.timeZone);
      invalidateDeviceData(queryClient);
    },
  });
}

export function useLifecycleEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ deviceId, body }: { deviceId: string; body: LifecycleEventInput }) =>
      postLifecycleEvent(deviceId, body),
    onSuccess: () => {
      invalidateDeviceData(queryClient);
    },
    onError: (error) => {
      if (deviceLifecycleErrorCode(error) === 'version_conflict') {
        invalidateDeviceData(queryClient);
      }
    },
  });
}

export function usePatchShipmentDates() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      deviceId,
      shipmentId,
      body,
    }: { deviceId: string; shipmentId: string; body: ShipmentDatesInput }) =>
      patchShipmentDates(deviceId, shipmentId, body),
    onSuccess: () => {
      invalidateDeviceData(queryClient);
    },
    onError: (error) => {
      if (deviceLifecycleErrorCode(error) === 'version_conflict') {
        invalidateDeviceData(queryClient);
      }
    },
  });
}

export function useBatchPatchDevices() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: batchPatchDevices,
    onSuccess: () => invalidateDeviceData(queryClient),
  });
}

export function useBatchDeleteDevices() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: batchDeleteDevices,
    onSuccess: () => invalidateDeviceData(queryClient),
  });
}

export function useBatchLifecycleEvents() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: batchLifecycleEvents,
    onSuccess: () => invalidateDeviceData(queryClient),
  });
}

export type DeviceBatchMutationInput = BatchPatchInput | BatchDeleteInput | BatchLifecycleInput;
